import Link from 'next/link'
import { useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'

import Button from '@nxt-insurance/ui-kit/components/Button'

import { colorEnum } from '@/cms/helperTypes'
import { webUrlSchema } from '@/cms/webUrl'
import { EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { useUrlFromWebUrlReference } from '@/hooks/useUrlFromWebUrlReference'
import { useWebUrlRel } from '@/hooks/useWebUrlRel'
import { itemsAlignmentTailwind } from '@/utils/style'
import type { PortableTextTypeComponentProps } from '@portabletext/react'

const buttonSchema = z.object({
  _type: z.literal('button'),
  type: z.enum(['default', 'disabled', 'danger']).optional(),
  style: z.enum(['text', 'default', 'outlined']).optional(),
  label: z.string(),
  align: z.enum(['left', 'center', 'right', 'stretch']).optional(),
  variantColor: colorEnum.optional(),
  target: webUrlSchema,
})

type ButtonSchema = z.infer<typeof buttonSchema>

export const ButtonRenderer = (props: {
  node: PortableTextTypeComponentProps<unknown>
  sectionName: string
  reverseOrderForWideScreen?: boolean
  align?: ButtonSchema['align']
}) => {
  const value = useMemo(() => buttonSchema.parse(props.node.value), [props.node.value])
  const { track } = useAnalytics()
  const align = value.align ?? props.align
  const href = useUrlFromWebUrlReference(value.target, { optional: true })
  const rel = useWebUrlRel(value.target)

  const variantColor = value.type === 'danger' ? 'uiRed' : value.variantColor ?? undefined

  return (
    <div
      className={twMerge(
        'flex flex-col',
        align && `w-full`,
        align && itemsAlignmentTailwind[align],
        props.reverseOrderForWideScreen && 'md:ltr'
      )}
    >
      {href ? (
        <Link
          className={twMerge(
            'flex flex-col',
            align && 'w-full',
            align && itemsAlignmentTailwind[align]
          )}
          href={href}
          passHref
          target={rel ? '_blank' : undefined}
          rel={rel}
        >
          <Button
            label={value.label}
            variantColor={variantColor}
            type={value.style ?? undefined}
            disabled={value.type === 'disabled'}
            onPress={() => {
              track(EVENTS.ButtonClicked, {
                label: value.label,
                href,
                sectionName: props.sectionName,
              })
            }}
          />
        </Link>
      ) : (
        <div>
          <Button
            label={value.label}
            variantColor={variantColor}
            type={value.style ?? undefined}
            disabled={value.type === 'disabled'}
            onPress={() => {
              track(EVENTS.ButtonClicked, {
                label: value.label,
                sectionName: props.sectionName,
              })
            }}
          />
        </div>
      )}
    </div>
  )
}
